import { useCallback, useEffect } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiAlertModal,
  MiBox,
  MiIcon,
  MiModal,
  MiSectionHeader,
  PriceRollup,
} from "@miplatform/components";
import { BUTTON_COLORS, BUTTON_VARIANTS } from "@miplatform/constants";
import { filterMaterialsByAttributes, simpleSort } from "@miplatform/utils";
import { mdiChevronDown, mdiFileOutline } from "@mdi/js";
import { SearchResultsContainer } from "@mibuildweb/components";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Skeleton,
  Typography,
} from "@mui/material";
import { ItemTemplatePlanArea } from "../../AddSpecSheet/ItemTemplatePlanArea/ItemTemplatePlanArea";
import { MaterialDrawer } from "../../AddSpecSheet/MaterialDrawer/MaterialDrawer";
import { useSpecMaterialFormViewModel } from "../../AddSpecSheet/SpecConfigForm/useSpecMaterialFormViewModel";
import { useImportPlanConfigViewModel } from "../ImportPlanConfig/useImportPlanConfigViewModel";
import { MaterialResultCard } from "../MaterialResultCard/MaterialResultCard";
import { ImportPlanAreaOptions } from "./ImportPlanAreaOptions/ImportPlanAreaOptions";
import { OptionsList } from "./OptionsList/OptionsList";
import { useImportPlanOptionsViewModel } from "./useImportPlanOptionsViewModel";

export function ImportPlanOptions({
  planDetails,
  stateManager,
  setPlanDetails,
}) {
  const { getFloors, planFloors, setPlanFloors, availableFloors } =
    useImportPlanConfigViewModel({ stateManager });

  const {
    selectedItems,
    materialOptions,
    categoryFilterOptions,
    selectedCategory,
    selectedAttributeValues,
    search,
    isSuppliedByBuilder,
    showCategoryFilter,
    debouncedSearch,
    debouncedItemTemplateSearch,
    hasFilters,
    materialResults,
    noResultsHelperText,
    openMaterialResults,
    itemTemplatesResult,
    selectedItemTemplate,
    setSelectedItemTemplate,
    itemTemplateSearch,
    templateFilter,
    seriesOptions,
    filteredAreaTypeOptions,
    openItemTemplateResults,
    getSeriesOptions,
    getAreaTypeOptions,
    getItemTemplates,
    handleAreaTypeOptionFilter,
    handleSeriesOptionFilter,
    setOpenItemTemplateResults,
    resetMaterialDrawerStates,
    setSelectedPlaceholder,
    setOpenMaterialResults,
    setShowCategoryFilter,
    setSelectedItems,
    setIsSuppliedByBuilder,
    handleCloseItemTemplateResults,
    setFilteredMaterialOptions,
    getCategoryFilters,
    getMaterialOptions,
    handleResetMaterialOptions,
    handleResetAddItems,
    handleCategoryClick,
    handleAttributeValueClick,
    handleItemTemplateSearch,
    handleItemSearch,
    isLoading,
  } = useSpecMaterialFormViewModel({
    stateManager,
    planDetails,
  });

  const {
    selectedOptionId,
    handleOptionClick,
    selectedOption,
    setSelectedOption,
    openRemoveOptionModal,
    setOpenRemoveOptionModal,
    handleRemoveOption,
    handleReviewed,
    handleRemoveOrReactivateOption,
  } = useImportPlanOptionsViewModel({
    stateManager,
    planDetails,
    setPlanDetails,
  });

  const { classes, theme } = useStyles();

  const getDisplayedPlanAreas = () => {
    return planDetails.planAreas || [];
  };

  const sortedPlanAreas = getDisplayedPlanAreas()?.sort((a, b) => {
    if (selectedOptionId) {
      return simpleSort(a.label, b.label);
    }
    return a.displayOrder - b.displayOrder;
  });

  const groupAreasByFloor = (areas) => {
    return areas.length ? Object.groupBy(areas, (area) => area.floorId) : {};
  };

  useEffect(() => {
    if (availableFloors?.length || sortedPlanAreas?.length > 0) {
      // derive saved floors from plan areas
      const planAreaFloors = availableFloors?.length
        ? Object.keys(groupAreasByFloor(sortedPlanAreas))?.map((key) =>
            availableFloors?.find((floor) => {
              return floor?.id === key;
            })
          )
        : [];
      const sortedPlanAreaFloors = planAreaFloors.sort((a, b) =>
        simpleSort(a?.displayOrder, b?.displayOrder)
      );
      setPlanFloors(sortedPlanAreaFloors);
    }
  }, [availableFloors?.length]);

  useEffect(() => {
    if (planDetails?.builderId) {
      handleResetAddItems();
      setOpenMaterialResults(false);
      getCategoryFilters();
      getAreaTypeOptions();
      getSeriesOptions();
      getFloors();
    }
  }, [planDetails?.builderId]);

  useEffect(() => {
    if (debouncedSearch || selectedCategory?.categoryId) {
      getMaterialOptions();
    }
    if ((!debouncedSearch || !selectedCategory?.categoryId) && !isLoading) {
      handleResetMaterialOptions();
    }
  }, [debouncedSearch, selectedCategory]);

  useEffect(() => {
    if (
      selectedCategory?.categoryId &&
      Object.keys(selectedAttributeValues).length > 0
    ) {
      // filter displayed material options by attribute value(s)
      setFilteredMaterialOptions(
        filterMaterialsByAttributes(materialOptions, selectedAttributeValues)
      );
    } else {
      // reset filtered material options to results from selected category and/or search term
      setFilteredMaterialOptions(materialOptions);
    }
  }, [materialOptions, selectedAttributeValues]);

  useEffect(() => {
    if (showCategoryFilter) {
      getItemTemplates();
    }
  }, [templateFilter, debouncedItemTemplateSearch]);

  const renderPlanAreas = (planAreas) => {
    return planAreas?.map((area) => {
      return (
        <ImportPlanAreaOptions
          key={area.id}
          area={area}
          stateManager={stateManager}
        />
      );
    });
  };

  const handleCloseCategoryFilter = useCallback(() => {
    setShowCategoryFilter(false);
    resetMaterialDrawerStates();
    setSelectedPlaceholder(null);
  }, []);

  return (
    <Box className={classes.root}>
      <MiBox className={classes.container} height="auto">
        <MiSectionHeader
          title="Configure Options"
          subtitle="Select available options and review plan impact. Approve the option, edit it, or create an additional option."
          rightAction={<PriceRollup data={planDetails} />}
        />
        <Box className={classes.flexContainer}>
          <Box className={classes.overflowContainer}>
            {planFloors?.map((floor) => {
              const floorAreas = sortedPlanAreas?.filter(
                (area) => area.floorId === floor.id
              );

              return (
                <Accordion
                  key={floor.id}
                  className={classes.floorCardContainer}
                  defaultExpanded
                  disableGutters
                >
                  <AccordionSummary
                    expandIcon={<MiIcon path={mdiChevronDown} />}
                  >
                    <Box className={classes.accordionSummaryBox}>
                      <Box>
                        {floor?.name ? (
                          <Typography
                            fontWeight={theme.fontWeight.bold}
                            paddingLeft={theme.spacing(2)}
                          >
                            {`${floor?.name} - ${floor?.displayOrder}`}
                          </Typography>
                        ) : (
                          <Skeleton
                            variant="text"
                            sx={{ fontSize: "2rem" }}
                            width={200}
                          />
                        )}
                      </Box>
                      <Box className={classes.floorCardHeaderContainer}>
                        <Typography variant="body1">
                          Areas: {floorAreas.length || 0}
                        </Typography>
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails className={classes.areaColumnsContainer}>
                    <Box className={classes.areaColumnContainer}>
                      {renderPlanAreas(floorAreas)}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Box>
          {!showCategoryFilter && (
            <OptionsList
              planDetails={planDetails}
              setShowCategoryFilter={setShowCategoryFilter}
              handleOptionClick={handleOptionClick}
              handleReviewed={handleReviewed}
              handleRemoveOrReactivateOption={handleRemoveOrReactivateOption}
            />
          )}
        </Box>
      </MiBox>
      <SearchResultsContainer
        open={openMaterialResults}
        alertTitle="Select Item(s) and add to Area(s)."
        alertDescription="After selecting the item, choose the Area(s) and select the Add to Area(s) button."
        storageKey="import-planMaterials-alert"
        results={materialResults}
        noResultsHelperText={noResultsHelperText}
        hasFilters={hasFilters}
        handleResetFilterSelection={handleResetAddItems}
        renderResults={() =>
          materialResults.map((material) => {
            return (
              <MaterialResultCard
                key={material.id}
                material={material}
                selectedItems={selectedItems}
                setSelectedItems={setSelectedItems}
                isSuppliedByBuilder={isSuppliedByBuilder}
                setIsSuppliedByBuilder={setIsSuppliedByBuilder}
              />
            );
          })
        }
      />
      <SearchResultsContainer
        open={openItemTemplateResults}
        onClose={handleCloseItemTemplateResults}
        alertTitle="Select Item Template(s) and add to area(s)."
        alertDescription="After selecting the item(s), choose the Area(s) and select the Add Selected Items button."
        storageKey="itemTemplatesMaterials-alert"
        isLoading={isLoading}
        results={itemTemplatesResult}
        hasFilters={hasFilters}
        renderResults={() =>
          itemTemplatesResult.map((template) => (
            <ItemTemplatePlanArea
              key={template.planAreaId}
              itemTemplate={template}
              setSelectedItemTemplate={setSelectedItemTemplate}
              selectedItemTemplate={selectedItemTemplate}
              openItemTemplateResults={openItemTemplateResults}
              selectedItems={selectedItems}
              setSelectedItems={setSelectedItems}
            />
          ))
        }
      />
      <MaterialDrawer
        open={showCategoryFilter}
        onClose={handleCloseCategoryFilter}
        planDetails={planDetails}
        resetMaterialDrawerStates={resetMaterialDrawerStates}
        // Category filter props
        search={search}
        handleItemSearch={handleItemSearch}
        categoryFilterOptions={categoryFilterOptions}
        selectedCategory={selectedCategory}
        selectedAttributeValues={selectedAttributeValues}
        handleCategoryClick={handleCategoryClick}
        handleAttributeValueClick={handleAttributeValueClick}
        // item template filter props
        itemTemplateSearch={itemTemplateSearch}
        handleItemTemplateSearch={handleItemTemplateSearch}
        templateFilter={templateFilter}
        seriesOptions={seriesOptions}
        filteredAreaTypeOptions={filteredAreaTypeOptions}
        handleSeriesOptionFilter={handleSeriesOptionFilter}
        handleAreaTypeOptionFilter={handleAreaTypeOptionFilter}
        setOpenItemTemplateResults={setOpenItemTemplateResults}
      />
      <MiModal
        open={openRemoveOptionModal}
        title="Remove Option?"
        description={
          <>
            You are about to remove this <b>{selectedOption?.name}</b> from the
            Bid draft. This modification will not impact the original Bid. Are
            you sure you would like to continue?
          </>
        }
        closeModal={() => {
          setOpenRemoveOptionModal(false);
          setSelectedOption(null);
        }}
        actions={[
          {
            name: "Cancel",
            variant: BUTTON_VARIANTS.TEXT,
            sx: { textDecoration: "underline" },
            color: BUTTON_COLORS.BLACK,
            onClick: () => {
              setOpenRemoveOptionModal(false);
              setSelectedOption(null);
            },
          },
          {
            name: "Yes, Remove",
            variant: BUTTON_VARIANTS.CONTAINED,
            color: BUTTON_COLORS.RED,
            onClick: () => {
              handleRemoveOption();
            },
          },
        ]}
      />
      {planDetails?.planId && (
        <MiAlertModal
          title="Options Review"
          subtitle="Review Plan details"
          description="Verify all Options are accurate."
          titleIcon={mdiFileOutline}
          position={{ top: "41%", left: "49%" }}
          storageKey={`import-bid-options-draft-${planDetails?.planId}`}
          closeLabel="Got it!"
        />
      )}
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    overflow: "auto",
    height: "100%",
  },
  container: {
    width: "100%",
    flex: 1,
    display: "flex",
    flexDirection: "column",
  },
  flexContainer: {
    display: "flex",
    gap: theme.spacing(2),
    height: "100%",
    overflow: "auto",
    padding: theme.spacing(0.5),
  },
  floorCardContainer: {
    backgroundColor: theme.palette.primary.light,
    marginBottom: theme.spacing(2),
    "&::before": {
      content: "none",
    },
  },
  floorCardHeaderContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  areaColumnContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  areaColumnsContainer: {
    display: "flex",
    gap: theme.spacing(2),
    flexWrap: "wrap",
  },
  overflowContainer: {
    display: "flex",
    flexDirection: "column",
    overflow: "auto",
    flex: 1,
    containerType: "inline-size",
    "& .MuiAccordion-root": {
      borderRadius: 8,
    },
    "& .MuiAccordionSummary-root:hover": {
      cursor: "default",
    },
  },
  accordionSummaryBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
}));
